import { todayStr } from "../../lib/date";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { getMedicalLogs, createMedicalLog, updateMedicalLog, deleteMedicalLog } from "../../lib/api";

const PERMISSION_LABELS = {
  standing: "Standing Permission",
  one_time_phone: "One-Time — Phone",
  one_time_text: "One-Time — Text",
  one_time_email: "One-Time — Email",
  parent_provided: "Parent Provided Medication",
};

const emptyForm = () => ({
  date: todayStr(),
  time: new Date().toTimeString().slice(0, 5),
  medication_name: "",
  dosage: "",
  reason: "",
  administered_by: "",
  permission_type: "standing",
  permission_notes: "",
  notes: "",
});

export function StudentMedicalTab({ studentId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const res = await getMedicalLogs({ student_id: studentId });
      setLogs(res.data);
    } catch {
      toast.error("Failed to load medical logs");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => { await reload(); if (alive) setLoading(false); })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (log) => {
    setEditingId(log.id);
    setForm({
      date: log.date || "", time: log.time || "", medication_name: log.medication_name || "",
      dosage: log.dosage || "", reason: log.reason || "", administered_by: log.administered_by || "",
      permission_type: log.permission_type || "standing", permission_notes: log.permission_notes || "",
      notes: log.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.medication_name.trim()) { toast.error("Medication / Treatment is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await updateMedicalLog(editingId, form);
        toast.success("Medical log updated");
      } else {
        await createMedicalLog({ ...form, student_id: studentId });
        toast.success("Medical log saved");
      }
      setDialogOpen(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save medical log");
    }
    setSaving(false);
  };

  const remove = async (log) => {
    if (!window.confirm("Delete this medical log entry?")) return;
    try { await deleteMedicalLog(log.id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>;

  return (
    <div className="space-y-4" data-testid="student-medical-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>OTC / Medical Logs</CardTitle>
              <CardDescription>{logs.length} entr{logs.length === 1 ? "y" : "ies"} — OTC medications and medical attention</CardDescription>
            </div>
            <Button onClick={openAdd} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="student-add-medical-btn">+ Add Medical Log</Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No medical logs for this student yet.</p>
          ) : (
            <div className="space-y-3">
              {logs.map((l) => (
                <div key={l.id} className="border rounded-md p-3 space-y-1" data-testid={`student-medical-${l.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="font-medium">{l.medication_name}{l.dosage ? ` · ${l.dosage}` : ""}</div>
                      <div className="text-xs text-muted-foreground">{l.date}{l.time ? ` ${l.time}` : ""}{l.administered_by ? ` · by ${l.administered_by}` : ""}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(l)} data-testid={`student-medical-edit-${l.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(l)} data-testid={`student-medical-delete-${l.id}`}>Delete</Button>
                    </div>
                  </div>
                  {l.reason && <div className="text-sm text-gray-700">Reason: {l.reason}</div>}
                  {l.permission_type && <div className="text-xs text-muted-foreground">Permission: {PERMISSION_LABELS[l.permission_type] || l.permission_type}{l.permission_notes ? ` — ${l.permission_notes}` : ""}</div>}
                  {l.notes && <div className="text-sm text-gray-700 whitespace-pre-wrap">{l.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="medical-log-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit Medical Log" : "Add Medical Log"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="medical-log-date" /></div>
              <div className="space-y-2"><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))} data-testid="medical-log-time" /></div>
            </div>
            <div className="space-y-2"><Label>Medication / Treatment *</Label><Input value={form.medication_name} onChange={(e) => setForm((p) => ({ ...p, medication_name: e.target.value }))} placeholder="e.g., Ibuprofen, Ice Pack" data-testid="medical-log-medication" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Dosage</Label><Input value={form.dosage} onChange={(e) => setForm((p) => ({ ...p, dosage: e.target.value }))} placeholder="e.g., 200mg" data-testid="medical-log-dosage" /></div>
              <div className="space-y-2"><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} placeholder="e.g., Headache" data-testid="medical-log-reason" /></div>
            </div>
            <div className="space-y-2"><Label>Administered By</Label><Input value={form.administered_by} onChange={(e) => setForm((p) => ({ ...p, administered_by: e.target.value }))} placeholder="Staff name" data-testid="medical-log-admin" /></div>
            <div className="space-y-2">
              <Label>Permission Type</Label>
              <Select value={form.permission_type} onValueChange={(v) => setForm((p) => ({ ...p, permission_type: v }))}>
                <SelectTrigger data-testid="medical-log-permission"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PERMISSION_LABELS).map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Permission Notes</Label><Input value={form.permission_notes} onChange={(e) => setForm((p) => ({ ...p, permission_notes: e.target.value }))} placeholder="Who gave permission, when" data-testid="medical-log-permission-notes" /></div>
            <div className="space-y-2"><Label>Additional Notes</Label><Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Any other notes" data-testid="medical-log-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={save} disabled={saving} data-testid="save-medical-log-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
